import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, IconButton, InputAdornment,
  InputLabel, MenuItem, Select, Snackbar, Tab, Tabs, TextField, Typography,
  Switch, FormControlLabel, Slider,
} from '@mui/material';
import {
  Add, ArrowBack, Bluetooth, BluetoothConnected, BluetoothDisabled, CallMerge,
  CheckCircle, ClearOutlined, Close, DarkMode, Keyboard, Kitchen,
  LightMode, LocalDining, LocalOffer, MergeType, NoteAlt, Payment, PauseCircle, PlayCircle,
  PointOfSale, Print, Receipt, Remove, Save, Search, ShoppingCart, Splitscreen, TableRestaurant,
  StarRate, ViewModule, ViewSidebar, VolumeOff, VolumeUp, ZoomIn, ZoomOut,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CartItem, MenuCategory, MenuItem as MenuItemType, Table } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useThemeModeContext } from '../hooks/useThemeMode';
import apiService from '../services/api';
import { kitchenTTS } from '../services/KitchenTTSService';
import { shadows, food, gradients } from '../theme';
import FloatingShapes from './common/FloatingShapes';
import bluetoothPrinter, { BillData, PrinterStatus } from '../services/BluetoothPrinterService';
import PrintManagerDialog from './PrintManagerDialog';
import { translateHindiToEnglish } from '../utils/foodTranslator';
import { BillPreviewModal } from './BillPreviewModal';
import { PaymentModal } from './PaymentModal';
import { POSThemeId, POS_THEME_LIST, POS_THEMES } from '../utils/posThemes';


type BillStatus = 'open' | 'held';
type SnackbarSeverity = 'success' | 'error';

interface POSCartItem extends CartItem {
  sentQuantity?: number;
  // Chosen size option (e.g. S/M/L) with its price. Undefined = item has no sizes.
  selectedSize?: { name: string; price: number };
}

// A cart line is uniquely identified by item + chosen size, so the same item in
// different sizes occupies separate lines.
const cartLineKey = (cartItem: POSCartItem) => `${cartItem.menuItem.id}|${cartItem.selectedSize?.name ?? ''}`;

// The chosen size travels to the kitchen/bill as a "Size: X" prefix in the order
// item's special instructions (no dedicated order column).
const SIZE_INSTRUCTION_RE = /^Size:\s*([^|]+?)\s*(?:\|\s*(.*))?$/;
const parseSizeInstructions = (raw?: string): { size: string | null; note: string } => {
  if (!raw) return { size: null, note: '' };
  const m = raw.match(SIZE_INSTRUCTION_RE);
  if (m) return { size: m[1].trim(), note: (m[2] || '').trim() };
  return { size: null, note: raw };
};
const buildSizeInstructions = (selectedSize?: { name: string }, note?: string): string | undefined => {
  const n = (note || '').trim();
  if (selectedSize) return n ? `Size: ${selectedSize.name} | ${n}` : `Size: ${selectedSize.name}`;
  return n || undefined;
};

interface AppliedCoupon {
  code: string;
  couponId: string;
  discountLabel: string;
  discountAmount: number;
}

interface BillSession {
  id: string;
  name: string;
  tableId?: string;
  tableNumber?: string;
  customerName: string;
  customerPhone: string;
  orderType?: 'DineIn' | 'Takeaway' | 'Delivery';
  status: BillStatus;
  cart: POSCartItem[];
  notes?: string;
  appliedCoupon?: AppliedCoupon;
  lastKotAt?: string;
  lastOrderId?: string;
  createdAt: string;
  updatedAt: string;
}


const IMAGE_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5134/api').replace('/api', '');

const STORAGE_KEY = 'menumaster.pos.billSessions';
const ACTIVE_BILL_KEY = 'menumaster.pos.activeBillId';
const MAX_CONCURRENT_BILLS = 6;

const createEmptyBill = (index: number): BillSession => ({
  id: `bill-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: `Bill ${index}`,
  customerName: '',
  customerPhone: '',
  orderType: 'DineIn',
  status: 'open',
  cart: [],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

const POS: React.FC = () => {
  const navigate = useNavigate();
  const { currentRestaurant } = useAuth();
  const { isDark, toggleMode } = useThemeModeContext();
  const restaurantId = currentRestaurant?.id || '';
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const lastAddRef = useRef<{ key: string; at: number } | null>(null);

  // ── POS visual theme ──────────────────────────────────────────────────────
  const [posTheme, setPosTheme] = useState<POSThemeId>(() =>
    (localStorage.getItem('menumaster.pos.theme') as POSThemeId) || 'classic'
  );
  const posT = POS_THEMES[posTheme];
  const effectiveIsDark = isDark || posT.isDark;

  // ── POS voice: announce when kitchen marks an order Ready ─────────────────
  const [posVoiceEnabled, setPosVoiceEnabled] = useState<boolean>(() => {
    try { return localStorage.getItem('pos_voice') !== 'off'; } catch { return true; }
  });
  const posVoiceRef    = useRef(posVoiceEnabled);
  const readyStatusRef = useRef<Map<string, string>>(new Map()); // orderId → last known status
  const posFirstRef    = useRef(true);
  useEffect(() => { posVoiceRef.current = posVoiceEnabled; }, [posVoiceEnabled]);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(0);
  const [sessions, setSessions] = useState<BillSession[]>([createEmptyBill(1)]);
  const [activeBillId, setActiveBillId] = useState('');
  const [tables, setTables] = useState<Table[]>([]);
  const [activeTableOrders, setActiveTableOrders] = useState<any[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItemType[]>([]);
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [autoSendKot, setAutoSendKot] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as SnackbarSeverity });
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [splitItemId, setSplitItemId] = useState('');
  const [splitQuantity, setSplitQuantity] = useState(1);
  const [splitTarget, setSplitTarget] = useState('new');
  const [mergeSourceId, setMergeSourceId] = useState('');
  // Item awaiting a size choice before being added to the bill (null = picker closed).
  const [sizePickerItem, setSizePickerItem] = useState<MenuItemType | null>(null);
  const [tempTableDialogOpen, setTempTableDialogOpen] = useState(false);
  const [tempTableNumber, setTempTableNumber] = useState('');
  const [tempTableCapacity, setTempTableCapacity] = useState('2');
  const [tempTableCreating, setTempTableCreating] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [openItemNotes, setOpenItemNotes] = useState<Set<string>>(new Set());
  const [orderNotesOpen, setOrderNotesOpen] = useState(false);

  const toggleItemNote = (itemId: string) =>
    setOpenItemNotes(prev => {
      const next = new Set(prev);
      next.has(itemId) ? next.delete(itemId) : next.add(itemId);
      return next;
    });

  // ── Billing flow state ───────────────────────────────────────────────────
  const [generatedBill, setGeneratedBill] = useState<any | null>(null);
  const [billPreviewOpen, setBillPreviewOpen] = useState<boolean>(false);
  const [newPaymentOpen, setNewPaymentOpen] = useState<boolean>(false);
  const [generatingBill, setGeneratingBill] = useState<boolean>(false);

  // ── Inline discount state ─────────────────────────────────────────────────
  const [discountType, setDiscountType] = useState<'Percent' | 'Flat' | null>(null);
  const [discountValue, setDiscountValue] = useState<number>(0);
  const [discountReason, setDiscountReason] = useState<string>('');
  const [applyServiceCharge, setApplyServiceCharge] = useState<boolean>(false);
  const [discountError, setDiscountError] = useState<string | null>(null);

  // ── GST config (loaded from backend) ────────────────────────────────────
  const [gstConfig, setGstConfig] = useState({ cgst: 2.5, sgst: 2.5, igst: 5.0, serviceChargeEnabled: false, serviceChargePercent: 5.0 });


  // ── Bluetooth printer state ──────────────────────────────────────────────
  const [printerStatus, setPrinterStatus] = useState<PrinterStatus>({ connected: false });
  const [printing, setPrinting] = useState(false);
  const [autoPrint, setAutoPrint] = useState(true);
  const [printManagerOpen, setPrintManagerOpen] = useState(false);
  const [pendingBillData, setPendingBillData] = useState<BillData | null>(null);

  // ── Show Images toggle state ──────────────────────────────────────────────
  const [showImages, setShowImages] = useState<boolean>(() => {
    return localStorage.getItem('menumaster.pos.showImages') === 'true';
  });

  // ── Language Selection state ─────────────────────────────────────────────
  const [posLanguage, setPosLanguage] = useState<'en' | 'hi'>(() => {
    return (localStorage.getItem('menumaster.pos.language') as 'en' | 'hi') || 'en';
  });

  // ── Sizing Scale state ────────────────────────────────────────────────────
  const [itemScale, setItemScale] = useState<number>(() => {
    const stored = localStorage.getItem('menumaster.pos.itemScale');
    return stored ? parseFloat(stored) : 1.0;
  });

  // ── Category view mode ───────────────────────────────────────────────────
  const [catViewMode, setCatViewMode] = useState<'tabs' | 'sidebar'>(() => {
    return (localStorage.getItem('menumaster.pos.catView') as 'tabs' | 'sidebar') || 'tabs';
  });

  // ── Favorites state ──────────────────────────────────────────────────────
  const [favorites, setFavorites] = useState<Set<string>>(() => {
    const stored = localStorage.getItem(`menumaster.favorites.${restaurantId}`);
    if (stored) {
      try {
        return new Set(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse favorites:', e);
      }
    }
    return new Set<string>();
  });

  const toggleFavorite = (itemId: string) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      localStorage.setItem(`menumaster.favorites.${restaurantId}`, JSON.stringify(Array.from(next)));
      return next;
    });
  };

  useEffect(() => {
    if (!restaurantId) return;
    const stored = localStorage.getItem(`menumaster.favorites.${restaurantId}`);
    if (stored) {
      try {
        setFavorites(new Set(JSON.parse(stored)));
      } catch (e) {
        console.error('Failed to parse favorites:', e);
        setFavorites(new Set());
      }
    } else {
      setFavorites(new Set());
    }
  }, [restaurantId]);

  // ── Resizable layout state ──────────────────────────────────────────────
  const [rightPanelWidth, setRightPanelWidth] = useState<number>(360);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = window.innerWidth - e.clientX - 16;
      if (newWidth >= 300 && newWidth <= 650) {
        setRightPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // ── Category sidebar resize ──────────────────────────────────────────────
  const [catSidebarWidth, setCatSidebarWidth] = useState<number>(() => {
    const s = localStorage.getItem('menumaster.pos.catSidebarWidth');
    return s ? parseInt(s, 10) : 90;
  });
  const [isSidebarDragging, setIsSidebarDragging] = useState(false);
  const sidebarDragStart = useRef<{ x: number; width: number } | null>(null);

  const startSidebarResize = (e: React.MouseEvent) => {
    e.preventDefault();
    sidebarDragStart.current = { x: e.clientX, width: catSidebarWidth };
    setIsSidebarDragging(true);
  };

  useEffect(() => {
    if (!isSidebarDragging) return;
    const onMove = (e: MouseEvent) => {
      if (!sidebarDragStart.current) return;
      const next = Math.min(160, Math.max(70, sidebarDragStart.current.width + (e.clientX - sidebarDragStart.current.x)));
      setCatSidebarWidth(next);
      localStorage.setItem('menumaster.pos.catSidebarWidth', String(next));
    };
    const onUp = () => setIsSidebarDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [isSidebarDragging]);

  useEffect(() => {
    const storedSessions = localStorage.getItem(STORAGE_KEY);
    const storedActiveId = localStorage.getItem(ACTIVE_BILL_KEY);
    if (storedSessions) {
      try {
        const parsed = JSON.parse(storedSessions) as BillSession[];
        if (parsed.length > 0) {
          setSessions(parsed);
          setActiveBillId(storedActiveId && parsed.some((s) => s.id === storedActiveId) ? storedActiveId : parsed[0].id);
          return;
        }
      } catch (storageError) {
        console.error('Failed to restore POS sessions:', storageError);
      }
    }
    const initialBill = createEmptyBill(1);
    setSessions([initialBill]);
    setActiveBillId(initialBill.id);
  }, []);

  // ── Register printer status callback once on mount ───────────────────────
  useEffect(() => {
    bluetoothPrinter.setStatusCallback(setPrinterStatus);
  }, []);

  useEffect(() => {
    if (sessions.length > 0) localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
  }, [sessions]);

  useEffect(() => {
    if (activeBillId) localStorage.setItem(ACTIVE_BILL_KEY, activeBillId);
  }, [activeBillId]);

  useEffect(() => {
    if (!restaurantId) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [tableData, categoryData, menuData, activeOrdersData, gstData] = await Promise.all([
          apiService.getTables(restaurantId),
          apiService.getMenuCategories(restaurantId),
          apiService.getMenuItems(undefined, restaurantId),
          apiService.getKitchenOrders(restaurantId),
          apiService.getGstConfig().catch(() => null),
        ]);
        setTables(tableData);
        setActiveTableOrders(activeOrdersData);
        setCategories(categoryData);
        setMenuItems(menuData);
        if (gstData) setGstConfig(gstData);
      } catch (fetchError: any) {
        console.error('Failed to load POS data:', fetchError);
        setError('Unable to load POS data right now.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [restaurantId]);

  // ── Poll for Ready orders and announce on POS ─────────────────────────────
  useEffect(() => {
    if (!restaurantId) return;
    posFirstRef.current = true;
    readyStatusRef.current = new Map();

    const poll = async () => {
      try {
        const orders: any[] = await apiService.getKitchenOrders(restaurantId);
        if (posFirstRef.current) {
          // Snapshot current statuses silently so we only announce future changes
          orders.forEach(o => readyStatusRef.current.set(o.id, String(o.status)));
          posFirstRef.current = false;
          return;
        }
        orders.forEach(o => {
          const normalized = (o.status === 4 || o.status === 'Ready') ? 'Ready' : String(o.status);
          const prev = readyStatusRef.current.get(o.id);
          if (normalized === 'Ready' && prev !== 'Ready' && posVoiceRef.current) {
            const orderNumber = o.orderNumber || `#${String(o.id).slice(-4).toUpperCase()}`;
            const items: Array<{ name: string; qty: number }> = (o.items || o.orderItems || []).map((i: any) => ({
              name: i.menuItemName || i.name || i.menuItem?.name || 'Item',
              qty:  i.quantity ?? 1,
            }));
            kitchenTTS.announceReady(orderNumber, items);
          }
          readyStatusRef.current.set(o.id, normalized);
        });
      } catch { /* keep polling on error */ }
    };

    poll();
    const id = setInterval(poll, 15_000);
    return () => clearInterval(id);
  }, [restaurantId]);

  const activeSession = sessions.find((session) => session.id === activeBillId) ?? sessions[0];
  
  const categoryTabs = useMemo(() => {
    const allLabel = posLanguage === 'hi' ? 'सभी आइटम' : 'All Items';
    const favLabel = posLanguage === 'hi' ? '★ पसंदीदा' : '★ Favorites';
    return [
      { id: 'all', name: allLabel },
      { id: 'favorites', name: favLabel },
      ...categories.map((item) => ({
        id: item.id,
        name: posLanguage === 'en' ? translateHindiToEnglish(item.name) : item.name
      }))
    ];
  }, [categories, posLanguage]);

  const filteredMenuItems = useMemo(() => menuItems.filter((item) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || item.name.toLowerCase().includes(q) || (item.shortcutName || '').toLowerCase().includes(q) || (item.description || '').toLowerCase().includes(q);
    
    const selectedTab = categoryTabs[activeCategory];
    if (!selectedTab) return matchesSearch;

    if (selectedTab.id === 'all') {
      return matchesSearch;
    } else if (selectedTab.id === 'favorites') {
      return matchesSearch && favorites.has(item.id);
    } else {
      return matchesSearch && item.categoryId === selectedTab.id;
    }
  }), [menuItems, searchQuery, activeCategory, categoryTabs, favorites]);

  const updateSessions = (updater: (current: BillSession[]) => BillSession[]) => {
    setSessions((current) => updater(current).map((session) => ({ ...session, updatedAt: new Date().toISOString() })));
  };

  const updateActiveSession = (updater: (session: BillSession) => BillSession) => {
    if (!activeSession) return;
    updateSessions((current) => current.map((session) => session.id === activeSession.id ? updater(session) : session));
  };

  const isHappyHourActive = (item: MenuItemType): boolean => {
    if (!(item as any).isHappyHourEnabled || !(item as any).happyHourPrice || !(item as any).happyHourStart || !(item as any).happyHourEnd) return false;
    try {
      const now = new Date();
      const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
      const happyDays: string = (item as any).happyHourDays || '';
      const daysList = happyDays ? happyDays.split(',').map((d: string) => d.trim().toLowerCase()) : [];
      if (daysList.length > 0 && !daysList.includes(currentDay)) return false;

      const timeString = now.toTimeString().split(' ')[0];
      const [currH, currM] = timeString.split(':').map(Number);
      const currMinutes = currH * 60 + currM;

      const startStr = String((item as any).happyHourStart);
      const endStr = String((item as any).happyHourEnd);

      const [startH, startM] = startStr.split(':').map(Number);
      const startMinutes = startH * 60 + startM;

      const [endH, endM] = endStr.split(':').map(Number);
      const endMinutes = endH * 60 + endM;

      return currMinutes >= startMinutes && currMinutes <= endMinutes;
    } catch {
      return false;
    }
  };

  const getEffectiveItemPrice = (item: MenuItemType): number => {
    return isHappyHourActive(item) ? ((item as any).happyHourPrice ?? item.price) : item.price;
  };

  // Price for a cart line: the chosen size's price when a size is selected,
  // otherwise the item's effective (happy-hour-aware) base price.
  const cartLinePrice = (cartItem: POSCartItem): number =>
    cartItem.selectedSize ? cartItem.selectedSize.price : getEffectiveItemPrice(cartItem.menuItem);

  const calculateSubtotal = (session: BillSession) => session.cart.reduce((sum, item) => sum + (cartLinePrice(item) * item.quantity), 0);
  const calculateTax = (session: BillSession) => {
    const sub = calculateSubtotal(session);
    const discount = calculateDiscount(session);
    const taxable = sub - discount;
    const isDelivery = session.orderType === 'Delivery';
    const rate = isDelivery ? gstConfig.igst : (gstConfig.cgst + gstConfig.sgst);
    return Math.round(taxable * (rate / 100) * 100) / 100;
  };
  const calculateDiscount = (session: BillSession) => session.appliedCoupon?.discountAmount ?? 0;
  const calculateTotal = (session: BillSession) => Math.round(Math.max(0, calculateSubtotal(session) + calculateTax(session) - calculateDiscount(session)));

  const applyCoupon = async () => {
    if (!activeSession || !couponInput.trim() || !restaurantId) return;
    setCouponLoading(true);
    try {
      const subtotal = calculateSubtotal(activeSession) + calculateTax(activeSession);
      const result = await apiService.validateCoupon({
        couponCode: couponInput.trim().toUpperCase(),
        restaurantId,
        orderAmount: subtotal,
      });
      if (result.isValid) {
        updateActiveSession((session) => ({
          ...session,
          appliedCoupon: {
            code: result.code,
            couponId: result.couponId,
            discountLabel: result.discountLabel,
            discountAmount: result.discountAmount,
          },
        }));
        setCouponInput('');
        setSnackbar({ open: true, message: `Coupon applied: ${result.discountLabel}`, severity: 'success' });
      } else {
        setSnackbar({ open: true, message: result.message || 'Invalid coupon', severity: 'error' });
      }
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.response?.data?.message || 'Failed to validate coupon', severity: 'error' });
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    updateActiveSession((session) => ({ ...session, appliedCoupon: undefined }));
    setSnackbar({ open: true, message: 'Coupon removed', severity: 'success' });
  };

  const glassCard = posTheme === 'classic'
    ? {
      background: isDark ? food.glassDark : food.glass,
      backdropFilter: 'blur(24px)',
      WebkitBackdropFilter: 'blur(24px)',
      border: `1px solid ${isDark ? food.glassDarkBorder : food.glassBorder}`,
      borderRadius: '12px',
      boxShadow: isDark ? shadows.glassDark : shadows.glass,
    }
    : {
      background: posT.surface,
      backdropFilter: 'blur(24px)',
      WebkitBackdropFilter: 'blur(24px)',
      border: `1px solid ${posT.border}`,
      borderRadius: '12px',
      boxShadow: posT.shadow,
    };

  const pillButton = {
    borderRadius: '9999px',
    boxShadow: isDark 
      ? '0 4px 0 rgba(239,68,68,0.35), 0 4px 8px rgba(0,0,0,0.25)' 
      : '0 4px 0 rgba(224, 42, 42, 0.2), 0 4px 12px rgba(224, 42, 42, 0.15)',
    transition: 'transform 0.12s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.12s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    position: 'relative',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: isDark 
        ? '0 6px 0 rgba(239,68,68,0.35), 0 8px 16px rgba(0,0,0,0.3)' 
        : '0 6px 0 rgba(224, 42, 42, 0.22), 0 8px 16px rgba(224, 42, 42, 0.2)',
    },
    '&:active': {
      transform: 'translateY(3px) scale(0.97)',
      boxShadow: isDark 
        ? '0 0px 0 rgba(0,0,0,0.35), 0 2px 4px rgba(0,0,0,0.15)' 
        : '0 0px 0 rgba(224, 42, 42, 0.2), 0 2px 4px rgba(224, 42, 42, 0.1)',
    }
  };

  const createNewBill = () => {
    if (sessions.length >= MAX_CONCURRENT_BILLS) {
      setSnackbar({ open: true, message: `Only ${MAX_CONCURRENT_BILLS} concurrent bills are allowed.`, severity: 'error' });
      return;
    }
    const nextBill = createEmptyBill(sessions.length + 1);
    setSessions((current) => [...current, nextBill]);
    setActiveBillId(nextBill.id);
    setSnackbar({ open: true, message: `${nextBill.name} opened.`, severity: 'success' });
  };

  const holdActiveBill = () => {
    if (!activeSession) return;
    updateActiveSession((session) => ({ ...session, status: 'held' }));
    const nextOpen = sessions.find((session) => session.id !== activeSession.id && session.status === 'open');
    if (nextOpen) {
      setActiveBillId(nextOpen.id);
    }
    setSnackbar({ open: true, message: `${activeSession.name} placed on hold.`, severity: 'success' });
  };

  const resumeBill = (billId: string) => {
    updateSessions((current) => current.map((session) => session.id === billId ? { ...session, status: 'open' } : session));
    setActiveBillId(billId);
    setSnackbar({ open: true, message: 'Bill resumed.', severity: 'success' });
  };

  const closeSession = (billId: string) => {
    updateSessions((current) => {
      const remaining = current.filter((session) => session.id !== billId);
      return remaining.length > 0 ? remaining : [createEmptyBill(1)];
    });
    if (activeBillId === billId) {
      const fallback = sessions.find((session) => session.id !== billId)?.id;
      setActiveBillId(fallback || '');
    }
  };

  const refreshTableStatus = async () => {
    if (!currentRestaurant?.id) return;
    try {
      const activeOrdersData = await apiService.getKitchenOrders(currentRestaurant.id);
      setActiveTableOrders(activeOrdersData);
    } catch (err) {
      console.error('Failed to refresh table status:', err);
    }
  };

  const openTempTableDialog = () => {
    const existingCount = tables.filter(t => t.isTemporary).length;
    setTempTableNumber(`TEMP-${existingCount + 1}`);
    setTempTableCapacity('2');
    setTempTableDialogOpen(true);
  };

  const createTempTable = async () => {
    if (!restaurantId || !tempTableNumber.trim()) return;
    setTempTableCreating(true);
    try {
      const created = await apiService.createTable({
        restaurantId,
        tableNumber: tempTableNumber.trim(),
        tableName: `Temp ${tempTableNumber.trim()}`,
        capacity: parseInt(tempTableCapacity) || 2,
        status: 1,
        isTemporary: true,
        isActive: true,
      } as any);
      setTempTableDialogOpen(false);
      const tableData = await apiService.getTables(restaurantId);
      setTables(tableData);
      // Auto-assign to current session
      assignTableToSession(created as Table);
      setSnackbar({ open: true, message: `Temporary table "${created.tableNumber}" created and assigned.`, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Could not create temporary table. Please try again.', severity: 'error' });
    } finally {
      setTempTableCreating(false);
    }
  };

  const assignTableToSession = async (table: Table) => {
    if (activeSession?.tableId === table.id) {
      updateSessions((current) => current.map((session) =>
        session.id === activeSession?.id ? { ...session, tableId: undefined, tableNumber: undefined } : session
      ));
      return;
    }

    const existing = sessions.find((session) => session.tableId === table.id);
    if (existing) {
      setActiveBillId(existing.id);
      if (existing.status === 'held') resumeBill(existing.id);
      return;
    }

    try {
      const activeOrders = await apiService.getKitchenOrders(restaurantId);
      const tableOrder = activeOrders.find(o => o.tableId === table.id || (o as any).TableId === table.id);

      if (tableOrder) {
        const orderAny = tableOrder as any;
        const dbCartItems: POSCartItem[] = (orderAny.orderItems || orderAny.OrderItems || []).map((oi: any) => {
          const matchedItem = menuItems.find(m => m.id === oi.menuItemId);
          const { size, note } = parseSizeInstructions(oi.specialInstructions || '');
          const unitPrice = oi.unitPrice || oi.price || 0;
          return {
            menuItem: matchedItem || {
              id: oi.menuItemId,
              name: oi.menuItem?.name || oi.menuItemName || 'Item',
              price: unitPrice,
              categoryId: oi.menuItem?.menuCategoryId || '',
              description: oi.menuItem?.description || '',
              tags: []
            },
            quantity: oi.quantity,
            sentQuantity: oi.quantity,
            specialInstructions: note,
            selectedSize: size ? { name: size, price: unitPrice } : undefined,
          };
        });

        const targetSessionId = activeSession ? activeSession.id : sessions[0]?.id;
        
        setSessions((prev) => prev.map((s) => s.id === targetSessionId ? {
          ...s,
          tableId: table.id,
          tableNumber: table.tableNumber,
          cart: dbCartItems,
          lastOrderId: orderAny.id,
          lastKotAt: orderAny.orderDate || orderAny.createdAt
        } : s));

        if (targetSessionId) {
          setActiveBillId(targetSessionId);
        }

        setSnackbar({ open: true, message: `Loaded active Captain order for Table ${table.tableNumber}!`, severity: 'success' });
        return;
      }
    } catch (err) {
      console.error("Failed to load active order for table:", err);
    }

    updateActiveSession((session) => ({ ...session, tableId: table.id, tableNumber: table.tableNumber }));
    setSnackbar({ open: true, message: `Table ${table.tableNumber} assigned to ${activeSession.name}.`, severity: 'success' });
  };

  // Add an item to the bill. If the item has size options, open the size picker
  // first; otherwise add it directly (unchanged behavior).
  const addItemToBill = (item: MenuItemType) => {
    if (item.sizes && item.sizes.length > 0) {
      setSizePickerItem(item);
      return;
    }
    addItemLine(item);
  };

  // Add a concrete cart line, optionally with a chosen size. Lines are merged by
  // item + size, so the same item in two sizes stays on two lines.
  const addItemLine = (item: MenuItemType, selectedSize?: { name: string; price: number }) => {
    const newLine: POSCartItem = { menuItem: item, quantity: 1, sentQuantity: 0, selectedSize };
    const key = cartLineKey(newLine);
    // Touchscreens can emit two click events for one tap. Ignore only an
    // immediate duplicate for the same line; two normal clicks still add qty 2.
    const now = Date.now();
    if (lastAddRef.current?.key === key && now - lastAddRef.current.at < 350) return;
    lastAddRef.current = { key, at: now };
    updateActiveSession((session) => {
      const matching = session.cart.filter((cartItem) => cartLineKey(cartItem) === key);
      if (matching.length > 0) {
        const existing = matching[0];
        const existingQuantity = matching.reduce((sum, cartItem) => sum + cartItem.quantity, 0);
        return {
          ...session,
          // Also heal duplicate persisted lines from earlier sessions.
          cart: [
            ...session.cart.filter((cartItem) => cartLineKey(cartItem) !== key),
            { ...existing, quantity: existingQuantity + 1 },
          ],
        };
      }
      return { ...session, cart: [...session.cart, newLine] };
    });
  };

  const updateCartItemQuantity = (lineKey: string, delta: number) => {
    updateActiveSession((session) => ({
      ...session,
      cart: session.cart
        .map((item) => cartLineKey(item) === lineKey ? { ...item, quantity: Math.max(0, item.quantity + delta) } : item)
        .filter((item) => item.quantity > 0),
    }));
  };

  const updateCartItemNote = (lineKey: string, note: string) => {
    updateActiveSession((session) => ({
      ...session,
      cart: session.cart.map((item) =>
        cartLineKey(item) === lineKey ? { ...item, specialInstructions: note } : item
      ),
    }));
  };

  const updateOrderNotes = (notes: string) => {
    updateActiveSession((session) => ({ ...session, notes }));
  };

  const clearCurrentBill = () => {
    updateActiveSession((session) => ({
      ...session,
      cart: [],
      customerName: '',
      customerPhone: '',
      tableId: undefined,
      tableNumber: undefined,
      lastKotAt: undefined,
      lastOrderId: undefined,
    }));
  };

  const handleSplitBill = () => {
    if (!activeSession || !splitItemId) return;
    const item = activeSession.cart.find((cartItem) => cartLineKey(cartItem) === splitItemId);
    if (!item || splitQuantity < 1 || splitQuantity >= item.quantity) {
      setSnackbar({ open: true, message: 'Choose a valid split quantity.', severity: 'error' });
      return;
    }

    let targetBillId = splitTarget;
    if (splitTarget === 'new') {
      if (sessions.length >= MAX_CONCURRENT_BILLS) {
        setSnackbar({ open: true, message: `Only ${MAX_CONCURRENT_BILLS} concurrent bills are allowed.`, severity: 'error' });
        return;
      }
      const nextBill = createEmptyBill(sessions.length + 1);
      setSessions((current) => [...current, nextBill]);
      targetBillId = nextBill.id;
    }

    updateSessions((current) => current.map((session) => {
      if (session.id === activeSession.id) {
        return {
          ...session,
          cart: session.cart.map((cartItem) => cartLineKey(cartItem) === splitItemId ? { ...cartItem, quantity: cartItem.quantity - splitQuantity } : cartItem),
        };
      }
      if (session.id === targetBillId) {
        const existing = session.cart.find((cartItem) => cartLineKey(cartItem) === splitItemId);
        if (existing) {
          return {
            ...session,
            cart: session.cart.map((cartItem) => cartLineKey(cartItem) === splitItemId ? { ...cartItem, quantity: cartItem.quantity + splitQuantity } : cartItem),
          };
        }
        return { ...session, tableId: session.tableId || activeSession.tableId, tableNumber: session.tableNumber || activeSession.tableNumber, cart: [...session.cart, { ...item, quantity: splitQuantity }] };
      }
      return session;
    }));

    setSplitDialogOpen(false);
    setSplitItemId('');
    setSplitQuantity(1);
    setSplitTarget('new');
    setSnackbar({ open: true, message: 'Bill split successfully.', severity: 'success' });
  };

  const handleMergeBills = () => {
    if (!activeSession || !mergeSourceId || mergeSourceId === activeSession.id) return;
    const source = sessions.find((session) => session.id === mergeSourceId);
    if (!source) return;

    updateSessions((current) => current
      .filter((session) => session.id !== mergeSourceId)
      .map((session) => {
        if (session.id !== activeSession.id) return session;
        const mergedCart = [...session.cart];
        source.cart.forEach((sourceItem) => {
          const existing = mergedCart.find((cartItem) => cartLineKey(cartItem) === cartLineKey(sourceItem));
          if (existing) existing.quantity += sourceItem.quantity;
          else mergedCart.push({ ...sourceItem });
        });
        return {
          ...session,
          cart: mergedCart,
          customerName: session.customerName || source.customerName,
          customerPhone: session.customerPhone || source.customerPhone,
          tableId: session.tableId || source.tableId,
          tableNumber: session.tableNumber || source.tableNumber,
        };
      }));

    setMergeDialogOpen(false);
    setMergeSourceId('');
    setSnackbar({ open: true, message: `${source.name} merged into ${activeSession.name}.`, severity: 'success' });
  };

  const createBackendOrder = async (session: BillSession) => {
    const discount = calculateDiscount(session);
    const existingOrderId = session.lastOrderId;

    let itemsToSend = session.cart;
    if (existingOrderId) {
      // Diff the cart items: only send the new quantity of items
      itemsToSend = session.cart
        .map((item) => ({
          ...item,
          quantity: item.quantity - (item.sentQuantity || 0)
        }))
        .filter((item) => item.quantity > 0);
    }

    // If there is an existing order and no new items to send,
    // we don't need to call createOrder. Just fetch the existing order.
    if (existingOrderId && itemsToSend.length === 0) {
      return await apiService.getOrderById(existingOrderId);
    }

    // Calculate the totals of the items being sent using configured GST rate
    const subtotal = itemsToSend.reduce((sum, item) => sum + (cartLinePrice(item) * item.quantity), 0);
    const isDelivery = session.orderType === 'Delivery';
    const gstRateForOrder = isDelivery ? gstConfig.igst : (gstConfig.cgst + gstConfig.sgst);
    const tax = Math.round(subtotal * (gstRateForOrder / 100) * 100) / 100;
    const total = Math.round(Math.max(0, subtotal + tax));

    const order = await apiService.createOrder({
      id: existingOrderId && existingOrderId.startsWith('offline-') ? existingOrderId : undefined,
      restaurantId: restaurantId || undefined,
      tableId: session.tableId || undefined,
      subtotal: subtotal,
      taxAmount: tax,
      discountAmount: existingOrderId ? 0 : discount,
      totalAmount: total,
      notes: session.notes || (session.lastKotAt ? 'Updated bill submission' : 'Initial POS submission'),
      orderType: session.orderType || 'DineIn',
      items: itemsToSend.map((item) => ({
        menuItemId: item.menuItem.id,
        quantity: item.quantity,
        unitPrice: cartLinePrice(item),
        totalPrice: cartLinePrice(item) * item.quantity,
        specialInstructions: buildSizeInstructions(item.selectedSize, item.specialInstructions),
      })),
    } as any);

    // Record coupon usage after order is created (only for new orders)
    if (session.appliedCoupon && order?.id && !existingOrderId) {
      try {
        await apiService.applyCoupon({
          couponCode: session.appliedCoupon.code,
          restaurantId,
          orderId: order.id,
          userId: '',
          orderAmount: calculateSubtotal(session) + calculateTax(session),
          discountAmount: discount,
        });
      } catch (couponErr) {
        console.warn('Coupon usage record failed:', couponErr);
      }
    }
    return order;
  };

  // ── Bluetooth printer helpers ────────────────────────────────────────────
  const handleConnectPrinter = async () => {
    if (printerStatus.connected) {
      bluetoothPrinter.disconnect();
      return;
    }
    if (!('bluetooth' in navigator)) {
      setSnackbar({ open: true, message: 'Web Bluetooth not supported. Use Chrome or Edge.', severity: 'error' });
      return;
    }
    const status = await bluetoothPrinter.connect();
    if (status.error && !status.error.includes('cancelled')) {
      setSnackbar({ open: true, message: `Printer error: ${status.error}`, severity: 'error' });
    } else if (status.connected) {
      setSnackbar({ open: true, message: `Connected to ${status.deviceName}`, severity: 'success' });
    }
  };

  const buildBillData = (session: BillSession, orderNumber?: string): BillData => {
    return {
      restaurantName: currentRestaurant?.name ?? 'Restaurant',
      restaurantAddress: currentRestaurant?.address,
      restaurantPhone: currentRestaurant?.phone,
      billNumber: orderNumber ?? session.name,
      date: new Date().toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
      customerName: session.customerName || undefined,
      customerPhone: session.customerPhone || undefined,
      tableNumber: session.tableNumber,
      items: session.cart.map((c) => {
        const baseName = posLanguage === 'en' ? translateHindiToEnglish(c.menuItem.name) : c.menuItem.name;
        return {
          name: c.selectedSize ? `${baseName} (${c.selectedSize.name})` : baseName,
          quantity: c.quantity,
          unitPrice: cartLinePrice(c),
          totalPrice: cartLinePrice(c) * c.quantity,
          specialInstructions: c.specialInstructions || undefined,
        };
      }),
      notes: session.notes || undefined,
      subtotal: calculateSubtotal(session),
      tax: calculateTax(session),
      discount: calculateDiscount(session),
      discountLabel: session.appliedCoupon?.code,
      total: calculateTotal(session),
    };
  };

  const handlePrintBill = async (orderNumber?: string) => {
    if (!activeSession || activeSession.cart.length === 0) {
      setSnackbar({ open: true, message: 'Nothing to print.', severity: 'error' });
      return;
    }
    if (!printerStatus.connected) {
      setSnackbar({ open: true, message: 'Connect a Bluetooth printer first.', severity: 'error' });
      return;
    }
    try {
      setPrinting(true);
      setSnackbar({ open: true, message: 'Printing…', severity: 'success' });
      await bluetoothPrinter.printBill(buildBillData(activeSession, orderNumber));
      setSnackbar({ open: true, message: 'Bill printed!', severity: 'success' });
    } catch (err: any) {
      const message = String(err?.message || 'Unknown printer error');
      setSnackbar({
        open: true,
        message: message.toLowerCase().includes('gatt')
          ? 'Printer connection dropped while sending. Keep the printer awake, reconnect it, and try again.'
          : `Print failed: ${message}`,
        severity: 'error'
      });
    } finally {
      setPrinting(false);
    }
  };

  const handleSendKot = async () => {
    if (!activeSession || activeSession.cart.length === 0) {
      setSnackbar({ open: true, message: 'Add items before sending KOT.', severity: 'error' });
      return;
    }
    try {
      setPlacingOrder(true);
      const createdOrder = await createBackendOrder(activeSession);
      const kotResult = await apiService.printKOT(createdOrder.id);

      // Server unreachable — fall back to direct Bluetooth print so the kitchen
      // gets the slip immediately instead of waiting for reconnection
      if (kotResult?.queued && printerStatus.connected) {
        const unsentItems = activeSession.cart
          .map(item => ({
            name: item.menuItem.name,
            quantity: item.quantity - ((item as any).sentQuantity ?? 0),
            specialInstructions: (item as any).specialInstructions,
          }))
          .filter(i => i.quantity > 0);
        await bluetoothPrinter.printKOT(
          unsentItems, activeSession.tableNumber, activeSession.name, activeSession.notes,
        ).catch(() => {});
      }

      updateActiveSession((session) => ({
        ...session,
        lastKotAt: new Date().toISOString(),
        lastOrderId: createdOrder.id,
        cart: session.cart.map(item => ({ ...item, sentQuantity: item.quantity }))
      }));
      setSnackbar({ open: true, message: `KOT sent for ${activeSession.name}.`, severity: 'success' });
    } catch (kotError: any) {
      console.error('Failed to send KOT:', kotError);
      setSnackbar({ open: true, message: `KOT failed: ${kotError.response?.data?.message || kotError.message}`, severity: 'error' });
    } finally {
      setPlacingOrder(false);
    }
  };

  const handleCheckout = () => {
    if (!activeSession || activeSession.cart.length === 0) {
      setSnackbar({ open: true, message: 'Cannot checkout an empty bill.', severity: 'error' });
      return;
    }
    if (discountValue > 0 && !discountReason.trim()) {
      setDiscountError('Discount reason is required');
      return;
    }
    handleGenerateBill(discountType, discountValue, discountReason, applyServiceCharge);
  };

  const handleGenerateBill = async (
    discountType: 'Percent' | 'Flat' | null,
    discountValue: number,
    discountReason: string,
    applyServiceCharge: boolean
  ) => {
    if (!activeSession) return;
    let orderId: string | undefined;
    try {
      setGeneratingBill(true);
      orderId = activeSession.lastOrderId;
      if (!orderId) {
        const createdOrder = await createBackendOrder(activeSession);
        orderId = createdOrder.id;
        updateActiveSession((session) => ({
          ...session,
          lastOrderId: createdOrder.id,
          cart: session.cart.map(item => ({ ...item, sentQuantity: item.quantity }))
        }));
      }

      const bill = await apiService.generateBill(orderId, {
        discountType,
        discountValue,
        discountReason: discountReason || null,
        applyServiceCharge
      });

      setGeneratedBill(bill);
      setBillPreviewOpen(true);
    } catch (err: any) {
      console.error(err);
      // If bill already generated for this order, fetch and show the existing one
      if (err?.response?.data?.code === 'ORDER_ALREADY_BILLED' && orderId) {
        try {
          const existingBill = await apiService.getBillByOrderId(orderId);
          setGeneratedBill(existingBill);
          setBillPreviewOpen(true);
          return;
        } catch {
          // fall through to snackbar below
        }
      }
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || err.message || 'Failed to generate bill',
        severity: 'error'
      });
    } finally {
      setGeneratingBill(false);
    }
  };

  const handlePaymentSuccess = async () => {
    setNewPaymentOpen(false);
    setBillPreviewOpen(false);
    setGeneratedBill(null);

    // Capture before clearing — needed for direct-pay completion below
    const directPayOrderId = activeSession?.lastOrderId;
    const wasDirectPay    = !!directPayOrderId && !activeSession?.lastKotAt;

    // If the paid session's table is temporary, delete it now
    const paidTableId = activeSession?.tableId;
    const paidTable = paidTableId ? tables.find(t => t.id === paidTableId) : null;
    const wasTemp = paidTable?.isTemporary;

    // Clear session details
    if (activeSession) {
      updateActiveSession((session) => ({
        ...session,
        cart: [],
        status: 'open',
        appliedCoupon: undefined,
        lastKotAt: undefined,
        lastOrderId: undefined,
        customerName: '',
        customerPhone: '',
        tableId: undefined,
        tableNumber: undefined
      }));
    }

    // Direct pay (no KOT) — mark order Served immediately so it doesn't appear in KDS
    if (wasDirectPay && directPayOrderId) {
      apiService.updateOrderStatus(directPayOrderId, 'Served' as any).catch(() => {});
    }

    if (wasTemp && paidTableId) {
      try {
        await apiService.deleteTable(paidTableId);
        setSnackbar({ open: true, message: `Payment recorded! Temporary table "${paidTable!.tableNumber}" removed.`, severity: 'success' });
      } catch {
        setSnackbar({ open: true, message: 'Payment recorded! (Could not auto-remove temp table)', severity: 'success' });
      }
    } else {
      setSnackbar({ open: true, message: 'Payment recorded! Table is now available.', severity: 'success' });
    }

    // Refresh table status list
    try {
      const tableData = await apiService.getTables(restaurantId);
      setTables(tableData);
    } catch (err) {
      console.warn('Failed to refresh tables:', err);
    }
  };


  useEffect(() => {
    const handleShortcuts = (event: KeyboardEvent) => {
      const tag = (event.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') {
        if (event.key === 'Escape') setSearchQuery('');
        return;
      }
      if (event.key === 'F2') { event.preventDefault(); createNewBill(); }
      if (event.key === 'F4') { event.preventDefault(); activeSession?.status === 'held' ? resumeBill(activeSession.id) : holdActiveBill(); }
      if (event.key === 'F7') { event.preventDefault(); handleSendKot(); }
      if (event.key === 'F8') { event.preventDefault(); handleCheckout(); }
      if (event.key === '/') { event.preventDefault(); searchInputRef.current?.focus(); }
    };
    window.addEventListener('keydown', handleShortcuts);
    return () => window.removeEventListener('keydown', handleShortcuts);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSession, sessions, restaurantId, autoSendKot]);

  const heldSessions = sessions.filter((session) => session.status === 'held');
  const mergeCandidates = sessions.filter((session) => session.id !== activeSession?.id);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', bgcolor: posTheme === 'classic' ? (isDark ? food.darkBg : food.bg) : posT.bg, backgroundImage: posTheme === 'classic' ? (isDark ? gradients.mesh.dark : gradients.mesh.light) : posT.mesh }}>
      <FloatingShapes />

      {/* Top bar */}
      <Box className="pos-top-bar" sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, zIndex: 10, ...glassCard, borderRadius: 0, borderLeft: 'none', borderRight: 'none', borderTop: 'none' }}>
        <IconButton size="small" onClick={() => navigate(-1)}><ArrowBack fontSize="small" /></IconButton>
        <PointOfSale fontSize="small" sx={{ color: 'primary.main' }} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>POS</Typography>
        <Chip label={`${sessions.length}/${MAX_CONCURRENT_BILLS} Bills`} size="small" variant="outlined" />

        {/* Print Manager chip — opens full printer dialog */}
        <Chip
          id="printer-status-chip"
          className="tactile-3d"
          icon={
            printerStatus.connected
              ? <BluetoothConnected fontSize="small" />
              : <Print fontSize="small" />
          }
          label={
            printing
              ? 'Printing…'
              : printerStatus.connected
              ? printerStatus.deviceName ?? 'Printer'
              : 'Print'
          }
          size="small"
          color={printerStatus.connected ? 'success' : 'primary'}
          variant={printerStatus.connected ? 'filled' : 'outlined'}
          onClick={() => { setPendingBillData(activeSession?.cart.length ? buildBillData(activeSession) : null); setPrintManagerOpen(true); }}
          sx={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.7rem' }}
        />
        <FormControlLabel
          className="tactile-3d"
          control={
            <Switch
              size="small"
              checked={showImages}
              onChange={(e) => {
                setShowImages(e.target.checked);
                localStorage.setItem('menumaster.pos.showImages', String(e.target.checked));
              }}
              color="primary"
            />
          }
          label="Show Images"
          sx={{ 
            '& .MuiFormControlLabel-label': { fontSize: '0.75rem', fontWeight: 600 },
            mr: 1
          }}
        />

        {/* Dynamic Scaling Slider */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, mr: 1, borderRadius: '8px', background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`, height: 28 }} className="tactile-3d">
          <ZoomOut fontSize="small" sx={{ color: 'text.secondary', fontSize: '0.85rem' }} />
          <Slider
            size="small"
            value={itemScale}
            min={0.8}
            max={1.6}
            step={0.1}
            onChange={(_, val) => {
              setItemScale(val as number);
              localStorage.setItem('menumaster.pos.itemScale', String(val));
            }}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
            sx={{ 
              width: 70, 
              color: 'primary.main',
              padding: '8px 0',
              '& .MuiSlider-thumb': {
                width: 12,
                height: 12,
                '&:before': {
                  boxShadow: '0 2px 4px rgba(0,0,0,0.4)',
                },
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: '0 0 0 6px rgba(239, 68, 68, 0.16)',
                },
                '&.Mui-active': {
                  boxShadow: '0 0 0 10px rgba(239, 68, 68, 0.24)',
                },
              },
              '& .MuiSlider-track': {
                border: 'none',
              },
              '& .MuiSlider-rail': {
                opacity: 0.3,
                backgroundColor: 'text.secondary',
              },
            }}
          />
          <ZoomIn fontSize="small" sx={{ color: 'text.secondary', fontSize: '0.85rem' }} />
        </Box>

        {/* Language Selection Select dropdown */}
        <FormControl size="small" variant="outlined" sx={{ minWidth: 100, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.75rem', height: 28 } }} className="tactile-3d">
          <Select
            value={posLanguage}
            onChange={(e) => {
              setPosLanguage(e.target.value as 'en' | 'hi');
              localStorage.setItem('menumaster.pos.language', e.target.value as string);
            }}
            sx={{ fontWeight: 600 }}
          >
            <MenuItem value="en" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>🇬🇧 English</MenuItem>
            <MenuItem value="hi" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>🇮🇳 हिन्दी</MenuItem>
          </Select>
        </FormControl>

        <IconButton
          className="tactile-3d"
          size="small"
          title={posVoiceEnabled ? 'Mute ready-order voice' : 'Unmute ready-order voice'}
          onClick={() => setPosVoiceEnabled(prev => {
            const next = !prev;
            if (!next) kitchenTTS.cancel();
            try { localStorage.setItem('pos_voice', next ? 'on' : 'off'); } catch {}
            return next;
          })}
          sx={{
            color: posVoiceEnabled ? 'primary.main' : 'text.disabled',
            border: '1px solid',
            borderColor: posVoiceEnabled ? 'primary.light' : 'divider',
            background: posVoiceEnabled ? 'primary.50' : 'transparent',
          }}
        >
          {posVoiceEnabled ? <VolumeUp fontSize="small" /> : <VolumeOff fontSize="small" />}
        </IconButton>
        <IconButton className="tactile-3d" size="small" onClick={toggleMode}>{isDark ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}</IconButton>

        {/* Bill Preview toggle */}
        <IconButton
          className="tactile-3d"
          size="small"
          title={generatedBill ? 'Open Bill Preview' : 'No bill generated yet'}
          disabled={!generatedBill}
          onClick={() => setBillPreviewOpen(true)}
          sx={{
            color: generatedBill ? 'primary.main' : 'text.disabled',
            border: '1px solid',
            borderColor: generatedBill ? 'primary.light' : 'divider',
            background: generatedBill ? 'primary.50' : 'transparent',
            borderRadius: '8px',
          }}
        >
          <Receipt fontSize="small" />
        </IconButton>

        <Chip
          icon={<Keyboard fontSize="small" />}
          label="F2 New · F4 Hold · F7 KOT · F8 Pay"
          size="small"
          variant="outlined"
          sx={{ display: { xs: 'none', md: 'flex' }, fontSize: '0.65rem' }}
        />
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mx: 2, mt: 1 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {/* Bill session tabs */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 1, zIndex: 9, overflowX: 'auto' }}>
        {sessions.map((session) => (
          <Chip
            key={session.id}
            className="tactile-3d"
            label={`${session.name}${session.tableNumber ? ` · T${session.tableNumber}` : ''}${session.status === 'held' ? ' ⏸' : ''}`}
            onClick={() => { setActiveBillId(session.id); if (session.status === 'held') resumeBill(session.id); }}
            onDelete={sessions.length > 1 ? () => closeSession(session.id) : undefined}
            color={session.id === activeBillId ? 'primary' : 'default'}
            variant={session.id === activeBillId ? 'filled' : 'outlined'}
            size="small"
            sx={{ fontWeight: session.id === activeBillId ? 700 : 400, color: session.id === activeBillId ? undefined : 'text.primary' }}
          />
        ))}
        {sessions.length < MAX_CONCURRENT_BILLS && (
          <IconButton className="tactile-3d" size="small" onClick={createNewBill} title="New Bill (F2)"><Add fontSize="small" /></IconButton>
        )}
      </Box>

      {/* Main content */}
      <Box sx={{ flex: 1, display: 'flex', gap: 1.5, p: 1.5, overflow: 'hidden', zIndex: 1 }}>

        {/* Left panel: Menu browser */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, ...glassCard, p: 1.5 }}>
          {/* Search + view toggle */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search items… (/)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              inputRef={searchInputRef}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              sx={{ flex: 1 }}
            />
            <IconButton
              size="small"
              title={catViewMode === 'tabs' ? 'Switch to Sidebar View' : 'Switch to Tab View'}
              onClick={() => {
                const next = catViewMode === 'tabs' ? 'sidebar' : 'tabs';
                setCatViewMode(next);
                localStorage.setItem('menumaster.pos.catView', next);
              }}
              sx={{
                border: '1px solid',
                borderColor: catViewMode === 'sidebar' ? 'primary.main' : 'divider',
                borderRadius: 1.5,
                color: catViewMode === 'sidebar' ? 'primary.main' : 'text.secondary',
                p: '5px',
              }}
            >
              {catViewMode === 'tabs' ? <ViewSidebar fontSize="small" /> : <ViewModule fontSize="small" />}
            </IconButton>
          </Box>

          {/* Category + Menu area — flex row in sidebar mode, column otherwise */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: catViewMode === 'sidebar' ? 'row' : 'column', gap: 1, overflow: 'hidden' }}>

            {/* ── Sidebar category panel (sidebar mode) ── */}
            {catViewMode === 'sidebar' && (() => {
              const SIDEBAR_COLORS = ['#E02A2A','#B71C1C','#388E3C','#D97706','#8B1111','#F97316','#14B8A6','#C2410C','#0F766E','#65A30D'];
              const circleSize = Math.round(Math.min(64, Math.max(36, catSidebarWidth * 0.52)));
              const fontSize = catSidebarWidth >= 130 ? '0.7rem' : '0.6rem';
              return (
                <>
                  <Box
                    sx={{
                      width: catSidebarWidth,
                      flexShrink: 0,
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                      '&::-webkit-scrollbar': { width: '3px' },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)', borderRadius: '4px' },
                    }}
                  >
                    {categoryTabs.map((cat, idx) => {
                      const isActive = activeCategory === idx;
                      const color = SIDEBAR_COLORS[idx % SIDEBAR_COLORS.length];
                      const emoji = cat.id === 'all' ? '★' : cat.id === 'favorites' ? '♥' : cat.name.charAt(0).toUpperCase();
                      return (
                        <Box
                          key={cat.id}
                          onClick={() => setActiveCategory(idx)}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            p: 0.75,
                            borderRadius: 2,
                            cursor: 'pointer',
                            border: '2px solid',
                            borderColor: isActive ? '#ef4444' : 'transparent',
                            background: isActive
                              ? (isDark ? 'rgba(239,68,68,0.12)' : 'rgba(239,68,68,0.06)')
                              : (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
                            transition: 'all 0.18s ease',
                            '&:hover': {
                              borderColor: isActive ? '#ef4444' : (isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'),
                              background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: circleSize,
                              height: circleSize,
                              borderRadius: '50%',
                              background: isActive ? '#ef4444' : color,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 0.5,
                              fontSize: `${circleSize * 0.42}px`,
                              color: '#fff',
                              fontWeight: 700,
                              boxShadow: isActive ? '0 3px 10px rgba(239,68,68,0.4)' : `0 2px 6px ${color}55`,
                              transition: 'all 0.18s ease',
                            }}
                          >
                            {emoji}
                          </Box>
                          <Typography
                            sx={{
                              fontSize,
                              fontWeight: isActive ? 700 : 500,
                              color: isActive ? '#ef4444' : (isDark ? '#e2e8f0' : '#475569'),
                              lineHeight: 1.2,
                              textAlign: 'center',
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              wordBreak: 'break-word',
                            }}
                          >
                            {cat.name}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Drag handle */}
                  <Box
                    onMouseDown={startSidebarResize}
                    sx={{
                      width: 6,
                      flexShrink: 0,
                      cursor: 'col-resize',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: isSidebarDragging
                        ? (isDark ? 'rgba(239,68,68,0.3)' : 'rgba(239,68,68,0.2)')
                        : 'transparent',
                      transition: 'background 0.15s',
                      userSelect: 'none',
                      '&:hover': {
                        background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {[0, 1, 2].map(i => (
                        <Box key={i} sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.18)' }} />
                      ))}
                    </Box>
                  </Box>
                </>
              );
            })()}

            {/* ── Right column: tabs (tab mode) + menu grid ── */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden' }}>

              {/* Horizontal category tabs (tabs mode only) */}
              {catViewMode === 'tabs' && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    py: 0.5,
                    overflowX: 'auto',
                    whiteSpace: 'nowrap',
                    zIndex: 2,
                    '&::-webkit-scrollbar': { height: '4px' },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                      borderRadius: '4px'
                    }
                  }}
                >
                  {categoryTabs.map((cat, idx) => {
                    const isActive = activeCategory === idx;
                    return (
                      <Button
                        key={cat.id}
                        onClick={() => setActiveCategory(idx)}
                        className="tactile-3d"
                        variant={isActive ? 'contained' : 'outlined'}
                        size="small"
                        sx={{
                          px: 2 * itemScale,
                          py: 0.5 * itemScale,
                          fontSize: `${0.75 * itemScale}rem`,
                          fontWeight: 700,
                          textTransform: 'none',
                          borderRadius: `${8 * itemScale}px`,
                          flexShrink: 0,
                          minWidth: 'auto',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          ...(isActive ? {
                            background: isDark
                              ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                              : 'linear-gradient(135deg, #e02a2a 0%, #b91c1c 100%)',
                            color: '#ffffff',
                            boxShadow: isDark
                              ? '0 4px 14px rgba(239, 68, 68, 0.4)'
                              : '0 4px 12px rgba(224, 42, 42, 0.3)',
                            border: 'none',
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              background: isDark
                                ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                                : 'linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)',
                            }
                          } : {
                            background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                            borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                            color: isDark ? '#e2e8f0' : '#475569',
                            '&:hover': {
                              background: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
                              borderColor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)',
                              transform: 'translateY(-1px)',
                            }
                          })
                        }}
                      >
                        {cat.name}
                      </Button>
                    );
                  })}
                </Box>
              )}

              {/* Menu items grid */}
              <Box sx={{ flex: 1, overflowY: 'auto', display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${140 * itemScale}px, 1fr))`, gap: 1 * itemScale, alignContent: 'start' }}>
            {filteredMenuItems.map((item) => (
              <motion.div key={item.id} whileHover={{ scale: 1.03 }}>
                <Card
                  onClick={() => addItemToBill(item)}
                  sx={{ 
                    cursor: 'pointer', 
                    p: 1.5 * itemScale, 
                    borderRadius: 1 * itemScale,
                    border: '1px solid', 
                    borderColor: isHappyHourActive(item) ? 'success.main' : 'divider', 
                    '&:hover': { borderColor: 'primary.main' },
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {showImages && (
                    <Box
                      sx={{
                        width: '100%',
                        height: 80 * itemScale,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        mb: 1,
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
                      }}
                    >
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl.startsWith('http') ? item.imageUrl : `${IMAGE_BASE}${item.imageUrl}`}
                          alt={item.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <LocalDining sx={{ fontSize: '1.75rem', color: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)' }} />
                      )}
                    </Box>
                  )}
                  {isHappyHourActive(item) && (
                    <Box sx={{ 
                      position: 'absolute', 
                      top: 0, 
                      right: 0, 
                      bgcolor: 'success.main', 
                      color: 'white', 
                      px: 0.75, 
                      py: 0.25, 
                      fontSize: '0.6rem', 
                      fontWeight: 800,
                      borderBottomLeftRadius: '6px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Happy Hour
                    </Box>
                  )}
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(item.id);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 4 * itemScale,
                      right: isHappyHourActive(item) ? 75 * itemScale : 4 * itemScale,
                      zIndex: 2,
                      color: favorites.has(item.id) ? '#ffb300' : (isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)'),
                      padding: `${2 * itemScale}px`,
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                      '&:hover': {
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.08)',
                      }
                    }}
                  >
                    <StarRate sx={{ fontSize: `${1 * itemScale}rem` }} />
                  </IconButton>
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ display: 'flex', alignItems: 'center', gap: 0.75 * itemScale, fontSize: `${0.875 * itemScale}rem` }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 12 * itemScale,
                        height: 12 * itemScale,
                        border: `${1 * itemScale}px solid ${item.isVegetarian ? '#2e7d32' : '#c62828'}`,
                        p: '1px',
                        borderRadius: `${2 * itemScale}px`,
                        flexShrink: 0
                      }}
                      title={item.isVegetarian ? 'Vegetarian' : 'Non-Vegetarian'}
                    >
                      <Box
                        sx={{
                          width: 6 * itemScale,
                          height: 6 * itemScale,
                          borderRadius: item.isVegetarian ? '50%' : '0%',
                          backgroundColor: item.isVegetarian ? '#2e7d32' : '#c62828',
                        }}
                      />
                    </Box>
                    {posLanguage === 'en' ? translateHindiToEnglish(item.name) : item.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: `${0.75 * itemScale}rem` }}>{posLanguage === 'en' && item.description ? translateHindiToEnglish(item.description) : (item.description || '')}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 * itemScale, mt: 0.5 * itemScale }}>
                    {item.sizes && item.sizes.length > 0 ? (
                      <Typography variant="body2" color="primary.main" fontWeight={700} sx={{ fontSize: `${0.875 * itemScale}rem` }}>
                        From ₹{Math.min(...item.sizes.map((s) => s.price)).toFixed(2)}
                      </Typography>
                    ) : isHappyHourActive(item) ? (
                      <>
                        <Typography variant="body2" color="success.main" fontWeight={800} sx={{ fontSize: `${0.875 * itemScale}rem` }}>
                          ₹{getEffectiveItemPrice(item).toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through', fontSize: `${0.75 * itemScale}rem` }}>
                          ₹{item.price.toFixed(2)}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="primary.main" fontWeight={700} sx={{ fontSize: `${0.875 * itemScale}rem` }}>
                        ₹{item.price.toFixed(2)}
                      </Typography>
                    )}
                  </Box>
                  {item.sizes && item.sizes.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 * itemScale }}>
                      {item.sizes.map((s) => (
                        <Chip key={s.name} label={s.name} size="small" sx={{ height: 16, fontSize: '0.6rem' }} />
                      ))}
                    </Box>
                  )}
                </Card>
              </motion.div>
            ))}
            {filteredMenuItems.length === 0 && (
              <Box sx={{ gridColumn: '1/-1', textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">No items found</Typography>
              </Box>
            )}
              </Box>
            </Box>{/* end right column */}
          </Box>{/* end category + menu area */}

          {/* Tables quick-assign */}
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 700, fontSize: '0.95rem' }}>
                <TableRestaurant sx={{ fontSize: '1.35rem', color: 'primary.main' }} /> Tables
              </Typography>
              <Chip
                label="+ Temp Table"
                size="small"
                onClick={openTempTableDialog}
                sx={{
                  fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                  border: '1.5px dashed #f59e0b',
                  background: 'rgba(245,158,11,0.1)',
                  color: '#f59e0b',
                  borderRadius: '8px',
                  '&:hover': { background: 'rgba(245,158,11,0.2)' },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {tables.slice(0, 12).map((table) => {
                const isOccupied = activeTableOrders.some((o: any) => o.tableId === table.id || (o as any).TableId === table.id);
                const isSelected = activeSession?.tableId === table.id;
                const isTemp = table.isTemporary;

                return (
                <Chip
                  key={table.id}
                  className="tactile-3d"
                  label={`${isTemp ? '⏱ ' : ''}T${table.tableNumber}${isOccupied ? ' (Occupied)' : ''}`}
                  variant={isSelected ? 'filled' : (isOccupied ? 'filled' : 'outlined')}
                  color={isSelected ? 'primary' : (isOccupied ? 'warning' : 'default')}
                  onClick={() => assignTableToSession(table)}
                  sx={{
                    px: 1.8, py: 2.2,
                    fontSize: '0.875rem', fontWeight: 700, borderRadius: '10px', cursor: 'pointer',
                    ...(isTemp && !isSelected ? {
                      border: '1.5px dashed #f59e0b',
                      background: 'rgba(245,158,11,0.08)',
                      color: '#f59e0b',
                    } : {}),
                    transition: 'all 0.2s ease',
                    '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }
                  }}
                />
              )})}
            </Box>
          </Box>
        </Box>

        {/* Resizable Divider */}
        <Box
          onMouseDown={startResize}
          sx={{
            width: '8px',
            cursor: 'col-resize',
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
            backgroundColor: isDragging ? 'rgba(239, 68, 68, 0.4)' : 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(239, 68, 68, 0.25)',
              '& .drag-handle': {
                backgroundColor: posT.accent
              }
            },
            transition: 'background-color 0.15s ease',
            position: 'relative',
            zIndex: 10,
            mx: -0.5,
            px: 0.5
          }}
        >
          <Box
            className="drag-handle"
            sx={{
              width: '4px',
              height: '40px',
              borderRadius: '2px',
                backgroundColor: isDragging ? posT.accent : isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
              transition: 'background-color 0.15s ease'
            }}
          />
        </Box>

        {/* Right panel: Active bill */}
        <Box
          sx={{
            width: { xs: '100%', sm: rightPanelWidth },
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            ...glassCard,
            p: 1.5,
            transition: isDragging ? 'none' : 'width 0.15s ease'
          }}
        >
          {/* Bill Rename & Order Type Toggle Selector */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
            <TextField
              size="small"
              label="Bill Name"
              value={activeSession?.name || ''}
              onChange={(e) => updateActiveSession((s) => ({ ...s, name: e.target.value }))}
              sx={{ flex: 1.2, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
              slotProps={{
                input: {
                  style: { fontSize: '0.8rem', fontWeight: 700 }
                }
              }}
            />
            <Box sx={{ display: 'flex', gap: 0.5, flex: 1.8 }}>
              {([
                { label: 'Dine In', value: 'DineIn' },
                { label: 'Takeaway', value: 'Takeaway' }
              ] as const).map((t) => {
                const isSelected = (activeSession?.orderType || 'DineIn') === t.value;
                return (
                  <Button
                    key={t.value}
                    size="small"
                    variant={isSelected ? 'contained' : 'outlined'}
                    className="tactile-3d"
                    onClick={() => {
                      updateActiveSession((s) => ({
                        ...s,
                        orderType: t.value,
                        ...(t.value === 'Takeaway' && { tableId: undefined, tableNumber: undefined })
                      }));
                      setSnackbar({ open: true, message: `Order type set to ${t.label}`, severity: 'success' });
                    }}
                    fullWidth
                    sx={{
                      borderRadius: '12px',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      py: 0.6,
                      textTransform: 'none',
                      ...(isSelected ? {
                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        color: '#ffffff',
                        boxShadow: isDark 
                          ? '0 3px 0 rgba(239,68,68,0.35), 0 3px 6px rgba(0,0,0,0.2)' 
                          : '0 3px 0 rgba(239,68,68,0.25), 0 3px 8px rgba(239,68,68,0.15)',
                      } : {
                        borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                        color: isDark ? '#e2e8f0' : '#475569',
                        boxShadow: 'none'
                      })
                    }}
                  >
                    {t.label}
                  </Button>
                );
              })}
            </Box>
          </Box>

          {/* Customer info */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              label="Customer"
              value={activeSession?.customerName || ''}
              onChange={(e) => updateActiveSession((s) => ({ ...s, customerName: e.target.value }))}
              sx={{ flex: 1 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><LocalDining fontSize="small" /></InputAdornment> }}
            />
            <TextField
              size="small"
              label="Phone"
              value={activeSession?.customerPhone || ''}
              onChange={(e) => updateActiveSession((s) => ({ ...s, customerPhone: e.target.value }))}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Cart */}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {!activeSession || activeSession.cart.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 4 }}>
                <ShoppingCart sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary" variant="body2">Cart is empty</Typography>
                <Typography color="text.disabled" variant="caption">Tap menu items to add</Typography>
              </Box>
            ) : (
              activeSession.cart.filter(item => (item.sentQuantity ?? 0) < item.quantity).map((cartItem) => {
                const lineKey = cartLineKey(cartItem);
                const linePrice = cartLinePrice(cartItem);
                const displayName = posLanguage === 'en' ? translateHindiToEnglish(cartItem.menuItem.name) : cartItem.menuItem.name;
                return (
                <Box key={lineKey} sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 10,
                            height: 10,
                            border: `1px solid ${cartItem.menuItem.isVegetarian ? '#2e7d32' : '#c62828'}`,
                            p: '1px',
                            borderRadius: '2px',
                            flexShrink: 0
                          }}
                        >
                          <Box
                            sx={{
                              width: 4,
                              height: 4,
                              borderRadius: cartItem.menuItem.isVegetarian ? '50%' : '0%',
                              backgroundColor: cartItem.menuItem.isVegetarian ? '#2e7d32' : '#c62828',
                            }}
                          />
                        </Box>
                        {displayName}
                        {cartItem.selectedSize && (
                          <Chip label={cartItem.selectedSize.name} size="small" sx={{ ml: 0.5, height: 16, fontSize: '0.6rem' }} />
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ₹{linePrice.toFixed(2)} × {cartItem.quantity}
                        {(cartItem.sentQuantity ?? 0) > 0 && (
                          <Chip label={`KOT: ${cartItem.sentQuantity}`} size="small" color="success" sx={{ ml: 0.5, height: 16, fontSize: '0.6rem' }} />
                        )}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={700} sx={{ minWidth: 60, textAlign: 'right' }}>
                      ₹{(linePrice * cartItem.quantity).toFixed(2)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        className="tactile-3d"
                        size="small"
                        onClick={() => toggleItemNote(lineKey)}
                        sx={{ color: (openItemNotes.has(lineKey) || cartItem.specialInstructions) ? 'warning.main' : 'text.disabled' }}
                        title="Add item note"
                      >
                        <NoteAlt fontSize="small" />
                      </IconButton>
                      <IconButton className="tactile-3d" size="small" onClick={() => updateCartItemQuantity(lineKey, -1)}><Remove fontSize="small" /></IconButton>
                      <Typography variant="body2" sx={{ minWidth: 20, textAlign: 'center' }}>{cartItem.quantity}</Typography>
                      <IconButton className="tactile-3d" size="small" onClick={() => updateCartItemQuantity(lineKey, 1)}><Add fontSize="small" /></IconButton>
                    </Box>
                  </Box>
                  {(openItemNotes.has(lineKey) || cartItem.specialInstructions) && (
                    <TextField
                      size="small"
                      placeholder={`Note for ${displayName}…`}
                      value={cartItem.specialInstructions || ''}
                      onChange={e => updateCartItemNote(lineKey, e.target.value)}
                      fullWidth
                      autoFocus={openItemNotes.has(lineKey) && !cartItem.specialInstructions}
                      sx={{ mt: 0.5 }}
                      slotProps={{ input: { style: { fontSize: '0.7rem' } } }}
                    />
                  )}
                </Box>
                );
              })
            )}
          </Box>

          <Divider />

          {/* Order-level notes */}
          {activeSession && activeSession.cart.length > 0 && (
            <Box>
              <Button
                size="small"
                startIcon={<NoteAlt fontSize="small" />}
                onClick={() => setOrderNotesOpen(v => !v)}
                variant={orderNotesOpen || activeSession.notes ? 'contained' : 'outlined'}
                color={activeSession.notes ? 'warning' : 'inherit'}
                sx={{ textTransform: 'none', fontSize: '0.72rem', fontWeight: 600, borderRadius: 2 }}
              >
                {activeSession.notes ? 'Order Note ✓' : 'Add Order Note'}
              </Button>
              {(orderNotesOpen || activeSession.notes) && (
                <TextField
                  size="small"
                  placeholder="Order notes for the kitchen…"
                  value={activeSession.notes || ''}
                  onChange={e => updateOrderNotes(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  autoFocus={orderNotesOpen && !activeSession.notes}
                  sx={{ mt: 0.75 }}
                  slotProps={{ input: { style: { fontSize: '0.75rem' } } }}
                />
              )}
            </Box>
          )}

          {/* Coupon input */}
          {activeSession && activeSession.cart.length > 0 && (
            <Box>
              {activeSession.appliedCoupon ? (
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  p: 1, borderRadius: '10px',
                  background: isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.08)',
                  border: '1px solid rgba(16,185,129,0.3)',
                }}>
                  <LocalOffer fontSize="small" sx={{ color: '#10b981' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#10b981', display: 'block' }}>
                      {activeSession.appliedCoupon.code}
                    </Typography>
                    <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                      {activeSession.appliedCoupon.discountLabel}
                    </Typography>
                  </Box>
                  <CheckCircle fontSize="small" sx={{ color: '#10b981' }} />
                  <IconButton size="small" onClick={removeCoupon} sx={{ p: 0.25 }}>
                    <ClearOutlined fontSize="small" sx={{ color: isDark ? '#94a3b8' : '#64748b' }} />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <TextField
                    size="small" placeholder="Enter coupon code" value={couponInput}
                    onChange={e => setCouponInput(e.target.value.toUpperCase())}
                    onKeyDown={e => e.key === 'Enter' && applyCoupon()}
                    slotProps={{
                      input: {
                        startAdornment: <InputAdornment position="start"><LocalOffer fontSize="small" /></InputAdornment>,
                        style: { fontSize: '0.75rem', letterSpacing: 1, fontWeight: 600, textTransform: 'uppercase' },
                      },
                    }}
                    sx={{ flex: 1 }}
                  />
                  <Button size="small" variant="outlined" onClick={applyCoupon}
                    disabled={couponLoading || !couponInput.trim()}
                    sx={{ ...pillButton, textTransform: 'none', fontWeight: 600, minWidth: 64 }}>
                    {couponLoading ? <CircularProgress size={14} /> : 'Apply'}
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* Totals */}
          {activeSession && activeSession.cart.length > 0 && (() => {
            const sub = calculateSubtotal(activeSession);
            const couponDisc = calculateDiscount(activeSession);
            const inlineDisc = discountType === 'Percent' && discountValue > 0
              ? Math.round(sub * (discountValue / 100) * 100) / 100
              : discountType === 'Flat' && discountValue > 0
                ? Math.min(discountValue, sub)
                : 0;
            const sc = applyServiceCharge ? Math.round(sub * 0.05 * 100) / 100 : 0;
            const taxable = sub - couponDisc - inlineDisc;
            const gstRate = activeSession.orderType === 'Delivery' ? gstConfig.igst : (gstConfig.cgst + gstConfig.sgst);
            const gst = Math.round(taxable * (gstRate / 100) * 100) / 100;
            const grand = Math.round(Math.max(0, taxable + gst + sc) * 100) / 100;
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">Subtotal</Typography>
                  <Typography variant="caption">₹{sub.toFixed(2)}</Typography>
                </Box>
                {activeSession.appliedCoupon && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: '#10b981' }}>Coupon ({activeSession.appliedCoupon.code})</Typography>
                    <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 700 }}>-₹{couponDisc.toFixed(2)}</Typography>
                  </Box>
                )}
                {inlineDisc > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: '#10b981' }}>
                      Discount ({discountType === 'Percent' ? `${discountValue}%` : 'Flat'})
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 700 }}>-₹{inlineDisc.toFixed(2)}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">GST ({gstRate}%)</Typography>
                  <Typography variant="caption">₹{gst.toFixed(2)}</Typography>
                </Box>
                {sc > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">Service Charge (5%)</Typography>
                    <Typography variant="caption">₹{sc.toFixed(2)}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2" fontWeight={700}>Total</Typography>
                  <Typography variant="subtitle2" fontWeight={700} color="primary.main">₹{grand.toFixed(0)}</Typography>
                </Box>
              </Box>
            );
          })()}

          {/* Inline discount controls */}
          {activeSession && activeSession.cart.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <Select
                    value={discountType || ''}
                    onChange={(e) => {
                      const t = (e.target.value as 'Percent' | 'Flat') || null;
                      setDiscountType(t);
                      setDiscountValue(0);
                      setDiscountError(null);
                    }}
                    displayEmpty
                    sx={{ fontSize: '0.75rem' }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.75rem' }}>No Discount</MenuItem>
                    <MenuItem value="Percent" sx={{ fontSize: '0.75rem' }}>% Percent Off</MenuItem>
                    <MenuItem value="Flat" sx={{ fontSize: '0.75rem' }}>₹ Flat Off</MenuItem>
                  </Select>
                </FormControl>
                {discountType && (
                  <TextField
                    size="small"
                    type="number"
                    value={discountValue || ''}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      if (val < 0) return;
                      const sub = calculateSubtotal(activeSession);
                      if (discountType === 'Percent' && val > 100) return;
                      if (discountType === 'Flat' && val > sub) { setDiscountError('Exceeds subtotal'); return; }
                      setDiscountError(null);
                      setDiscountValue(val);
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start" sx={{ '& p': { fontSize: '0.75rem' } }}>{discountType === 'Percent' ? '%' : '₹'}</InputAdornment>
                    }}
                    inputProps={{ min: 0, style: { fontSize: '0.75rem' } }}
                    sx={{ flex: 1 }}
                    error={!!discountError && !discountError.includes('reason')}
                    helperText={discountError && !discountError.includes('reason') ? discountError : ''}
                  />
                )}
              </Box>
              {discountType && discountValue > 0 && (
                <TextField
                  size="small"
                  placeholder="Discount reason (required)"
                  value={discountReason}
                  onChange={(e) => { setDiscountReason(e.target.value); if (e.target.value.trim()) setDiscountError(null); }}
                  error={!!discountError && discountError.includes('reason')}
                  helperText={discountError && discountError.includes('reason') ? discountError : ''}
                  InputProps={{ startAdornment: <InputAdornment position="start"><LocalOffer sx={{ fontSize: '0.875rem' }} /></InputAdornment> }}
                  inputProps={{ style: { fontSize: '0.75rem' } }}
                />
              )}
              <FormControlLabel
                control={<Switch size="small" checked={applyServiceCharge} onChange={(e) => setApplyServiceCharge(e.target.checked)} />}
                label={<Typography variant="caption" color="text.secondary">Service Charge (5%)</Typography>}
                sx={{ ml: 0, mt: -0.5 }}
              />
            </Box>
          )}

          {/* Bottom actions container for premium hover slide */}
          <Box className="pos-bottom-actions" sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 'auto' }}>
            {/* Auto KOT + Auto Print toggles */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`Auto KOT: ${autoSendKot ? 'ON' : 'OFF'}`}
                size="small"
                color={autoSendKot ? 'success' : 'default'}
                variant="outlined"
                onClick={() => setAutoSendKot((v) => !v)}
              />
              <Chip
                icon={<Print fontSize="inherit" />}
                label={`Auto Print: ${autoPrint ? 'ON' : 'OFF'}`}
                size="small"
                color={autoPrint ? 'info' : 'default'}
                variant="outlined"
                onClick={() => setAutoPrint((v) => !v)}
              />
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                <IconButton
                  size="small"
                  className="tactile-3d"
                  title="Speak order"
                  disabled={!activeSession?.cart.some(item => (item.sentQuantity ?? 0) < item.quantity)}
                  onClick={() => {
                    if (!activeSession) return;
                    const tableLabel = activeSession.tableNumber ? `Table ${activeSession.tableNumber}` : activeSession.name;
                    const items = activeSession.cart
                      .filter(item => (item.sentQuantity ?? 0) < item.quantity)
                      .map(item => ({ name: item.menuItem.name, qty: item.quantity - ((item as any).sentQuantity ?? 0) }));
                    kitchenTTS.announceOrder(tableLabel, items);
                  }}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, flexShrink: 0 }}
                >
                  <VolumeUp fontSize="small" />
                </IconButton>
                <Button
                  variant="outlined"
                  size="small"
                  className="tactile-3d"
                  startIcon={<Kitchen fontSize="small" />}
                  onClick={handleSendKot}
                  disabled={placingOrder || !activeSession?.cart.length}
                  fullWidth
                  sx={{
                    ...pillButton,
                    ...(posTheme !== 'classic' ? {
                      borderColor: posT.accent,
                      color: posT.accent,
                      '&:hover': { borderColor: posT.accent, background: `${posT.accent}10` },
                    } : {}),
                  }}
                >
                  KOT (F7)
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  className="tactile-3d"
                  startIcon={placingOrder ? <CircularProgress size={14} color="inherit" /> : <Payment fontSize="small" />}
                  onClick={handleCheckout}
                  disabled={placingOrder || !activeSession?.cart.length}
                  fullWidth
                  color="success"
                  sx={{
                    ...pillButton,
                    ...(posTheme !== 'classic' ? {
                      background: posT.payGradient,
                      '&:hover': { background: posT.payGradient, opacity: 0.9, boxShadow: `0 6px 16px ${posT.accent}40` },
                    } : {}),
                  }}
                >
                  Pay (F8)
                </Button>
              </Box>

              {/* KOT & Print Combined Option */}
              <Button
                variant="contained"
                size="small"
                className="tactile-3d"
                startIcon={placingOrder || printing ? <CircularProgress size={14} color="inherit" /> : <PointOfSale fontSize="small" />}
                onClick={async () => {
                  if (!activeSession || activeSession.cart.length === 0) return;
                  try {
                    setPlacingOrder(true);
                    // 1. Create order and send KOT
                    const createdOrder = await createBackendOrder(activeSession);
                    try {
                      const kotResult = await apiService.printKOT(createdOrder.id);
                      if (kotResult?.queued && printerStatus.connected) {
                        const unsentItems = activeSession.cart
                          .map(item => ({
                            name: item.menuItem.name,
                            quantity: item.quantity - ((item as any).sentQuantity ?? 0),
                            specialInstructions: (item as any).specialInstructions,
                          }))
                          .filter(i => i.quantity > 0);
                        await bluetoothPrinter.printKOT(
                          unsentItems, activeSession.tableNumber, activeSession.name, activeSession.notes,
                        ).catch(() => {});
                      }
                    } catch (e) {
                      console.warn('KOT print error:', e);
                    }
                    
                    updateActiveSession((session) => ({
                      ...session,
                      lastKotAt: new Date().toISOString(),
                      lastOrderId: createdOrder.id,
                      cart: session.cart.map(item => ({ ...item, sentQuantity: item.quantity }))
                    }));

                    // 2. Print bill via Bluetooth if connected, or open print manager
                    if (printerStatus.connected) {
                      await bluetoothPrinter.printBill(buildBillData(activeSession, createdOrder.orderNumber));
                      setSnackbar({ open: true, message: 'KOT Sent & Bill Printed Successfully!', severity: 'success' });
                    } else {
                      setPendingBillData(buildBillData(activeSession, createdOrder.orderNumber));
                      setPrintManagerOpen(true);
                      setSnackbar({ open: true, message: 'KOT Sent! Opened Print Manager.', severity: 'success' });
                    }
                  } catch (err: any) {
                    const message = String(err?.message || 'Unknown printer error');
                    setSnackbar({
                      open: true,
                      message: message.toLowerCase().includes('gatt')
                        ? 'Printer connection dropped while sending. Reconnect the printer and try again.'
                        : `KOT & Print Failed: ${message}`,
                      severity: 'error'
                    });
                  } finally {
                    setPlacingOrder(false);
                  }
                }}
                disabled={placingOrder || !activeSession?.cart.length}
                fullWidth
                sx={{
                  ...pillButton,
                  background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                  color: '#ffffff',
                  boxShadow: isDark 
                    ? '0 4px 0 rgba(239,68,68,0.35), 0 4px 8px rgba(0,0,0,0.25)' 
                    : '0 4px 0 rgba(239,68,68,0.25), 0 4px 12px rgba(239, 68, 68, 0.15)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)',
                    boxShadow: isDark 
                      ? '0 6px 0 rgba(239, 68, 68, 0.35), 0 8px 16px rgba(0, 0, 0, 0.3)' 
                      : '0 6px 0 rgba(239, 68, 68, 0.25), 0 8px 16px rgba(239, 68, 68, 0.2)',
                  }
                }}
              >
                KOT & Print
              </Button>

              {/* Print Manager button — opens full printer dialog */}
              <Button
                id="print-bill-btn"
                variant="outlined"
                size="small"
                className="tactile-3d"
                startIcon={<Print fontSize="small" />}
                onClick={() => {
                  setPendingBillData(activeSession?.cart.length ? buildBillData(activeSession) : null);
                  setPrintManagerOpen(true);
                }}
                fullWidth
                color="primary"
                sx={{ ...pillButton }}
              >
                Print / Printers
              </Button>

              <Box sx={{ display: 'flex', gap: 0.75 }}>
                <Button
                  variant="outlined"
                  size="small"
                  className="tactile-3d"
                  startIcon={activeSession?.status === 'held' ? <PlayCircle fontSize="small" /> : <PauseCircle fontSize="small" />}
                  onClick={() => activeSession?.status === 'held' ? resumeBill(activeSession.id) : holdActiveBill()}
                  disabled={!activeSession}
                  fullWidth
                  sx={pillButton}
                >
                  {activeSession?.status === 'held' ? 'Resume (F4)' : 'Hold (F4)'}
                </Button>
                <Button variant="outlined" size="small" className="tactile-3d" startIcon={<Save fontSize="small" />} onClick={clearCurrentBill} disabled={!activeSession?.cart.length} fullWidth color="warning" sx={pillButton}>
                  Clear
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                <Button
                  variant="outlined"
                  size="small"
                  className="tactile-3d"
                  startIcon={<Splitscreen fontSize="small" />}
                  onClick={() => setSplitDialogOpen(true)}
                  disabled={!activeSession?.cart.length}
                  fullWidth
                  sx={pillButton}
                >
                  Split
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  className="tactile-3d"
                  startIcon={<MergeType fontSize="small" />}
                  onClick={() => setMergeDialogOpen(true)}
                  disabled={mergeCandidates.length === 0}
                  fullWidth
                  sx={pillButton}
                >
                  Merge
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Held bills */}
          {heldSessions.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PauseCircle fontSize="inherit" /> On Hold
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {heldSessions.map((s) => (
                  <Chip
                    key={s.id}
                    label={`${s.name}${s.tableNumber ? ` T${s.tableNumber}` : ''} · ₹${calculateTotal(s).toFixed(0)}`}
                    size="small"
                    variant="outlined"
                    icon={<PlayCircle fontSize="small" />}
                    onClick={() => resumeBill(s.id)}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Size picker — shown when adding an item that has size options */}
      <Dialog open={!!sizePickerItem} onClose={() => setSizePickerItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Choose size — {sizePickerItem?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
            {(sizePickerItem?.sizes || []).map((sz) => (
              <Button
                key={sz.name}
                variant="outlined"
                size="large"
                onClick={() => {
                  if (sizePickerItem) addItemLine(sizePickerItem, { name: sz.name, price: sz.price });
                  setSizePickerItem(null);
                }}
                sx={{ justifyContent: 'space-between', borderRadius: '10px', px: 2 }}
              >
                <span>{sz.name}</span>
                <span>₹{sz.price.toFixed(2)}</span>
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSizePickerItem(null)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Split Bill Dialog */}
      <Dialog open={splitDialogOpen} onClose={() => setSplitDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Splitscreen fontSize="small" /> Split Bill
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Item to split</InputLabel>
              <Select value={splitItemId} label="Item to split" onChange={(e) => setSplitItemId(e.target.value)}>
                {(activeSession?.cart || []).filter((item) => item.quantity > 1).map((item) => (
                  <MenuItem key={cartLineKey(item)} value={cartLineKey(item)}>
                    {item.menuItem.name}{item.selectedSize ? ` (${item.selectedSize.name})` : ''} (qty: {item.quantity})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Quantity to move"
              type="number"
              value={splitQuantity}
              onChange={(e) => setSplitQuantity(Number(e.target.value))}
              inputProps={{ min: 1 }}
              fullWidth
            />
            <FormControl fullWidth size="small">
              <InputLabel>Move to</InputLabel>
              <Select value={splitTarget} label="Move to" onChange={(e) => setSplitTarget(e.target.value)}>
                <MenuItem value="new">New bill</MenuItem>
                {mergeCandidates.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSplitDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSplitBill} variant="contained" disabled={!splitItemId}>Split</Button>
        </DialogActions>
      </Dialog>

      {/* Merge Bills Dialog */}
      <Dialog open={mergeDialogOpen} onClose={() => setMergeDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CallMerge fontSize="small" /> Merge Bills
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Select a bill to merge into <strong>{activeSession?.name}</strong>:
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Source bill</InputLabel>
              <Select value={mergeSourceId} label="Source bill" onChange={(e) => setMergeSourceId(e.target.value)}>
                {mergeCandidates.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}{s.tableNumber ? ` · T${s.tableNumber}` : ''} — {s.cart.length} item(s)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleMergeBills} variant="contained" disabled={!mergeSourceId}>Merge</Button>
        </DialogActions>
      </Dialog>

      {/* Bill Preview Modal */}
      <BillPreviewModal
        open={billPreviewOpen}
        onClose={() => { setBillPreviewOpen(false); setGeneratedBill(null); }}
        bill={generatedBill}
        restaurantName={currentRestaurant?.name}
        posTheme={posTheme}
        onThemeChange={(t) => {
          setPosTheme(t);
          localStorage.setItem('menumaster.pos.theme', t);
        }}
        customerPhone={activeSession?.customerPhone || ''}
        onProceedToPay={() => {
          setBillPreviewOpen(false);
          setNewPaymentOpen(true);
        }}
        onCancelBill={async (billId) => {
          try {
            await apiService.cancelBill(billId, 'Regenerating with different parameters');
            setBillPreviewOpen(false);
            setGeneratedBill(null);
          } catch (err: any) {
            setSnackbar({
              open: true,
              message: err?.response?.data?.message || 'Failed to cancel bill',
              severity: 'error'
            });
          }
        }}
      />

      {/* Payment Modal */}
      {generatedBill && (
        <PaymentModal
          open={newPaymentOpen}
          onClose={() => setNewPaymentOpen(false)}
          billId={generatedBill.billId}
          grandTotal={generatedBill.grandTotal}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Print Manager Dialog */}
      <PrintManagerDialog
        open={printManagerOpen}
        onClose={() => setPrintManagerOpen(false)}
        bill={pendingBillData}
      />

      {/* Temp Table Dialog */}
      <Dialog
        open={tempTableDialogOpen}
        onClose={() => setTempTableDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TableRestaurant sx={{ color: '#f59e0b' }} />
          Create Temporary Table
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#f59e0b', mb: 2, p: 1.25, background: 'rgba(245,158,11,0.08)', borderRadius: '8px', border: '1px dashed rgba(245,158,11,0.4)' }}>
            This table is for the <strong>current bill only</strong>. It will be removed automatically once payment is collected.
          </Typography>
          <TextField
            label="Table Number / Name"
            fullWidth
            size="small"
            value={tempTableNumber}
            onChange={e => setTempTableNumber(e.target.value)}
            placeholder="e.g. TEMP-1, Lawn-Extra, Balcony-2"
            sx={{ mb: 2 }}
            autoFocus
          />
          <TextField
            label="Seats / Capacity"
            fullWidth
            size="small"
            type="number"
            value={tempTableCapacity}
            onChange={e => setTempTableCapacity(e.target.value)}
            inputProps={{ min: 1, max: 50 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setTempTableDialogOpen(false)} variant="outlined" sx={{ borderRadius: '10px' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!tempTableNumber.trim() || tempTableCreating}
            onClick={createTempTable}
            sx={{ borderRadius: '10px', background: '#f59e0b', '&:hover': { background: '#d97706' }, fontWeight: 700 }}
          >
            {tempTableCreating ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Create & Assign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default POS;
